import {DateTime, DateTime as LuxonDateTime, DurationLike, DurationUnits, ToISOTimeOptions} from "luxon"

enum PredefinedFormats {
    ISO_FILE = "yyyy-MM-dd-HH-mm-ss-SSS",
    DAY_ISO = "yyyy-MM-dd",
    GERMAN_DATE = "dd.MM.yyyy",
    GERMAN_DATE_TIME = "dd.MM.yyyy HH:mm",

}

export class RIODateTime {
    public static readonly PREDEFINED_FORMATS = PredefinedFormats

    private readonly luxonDateTime: LuxonDateTime

    private constructor(luxonDateTime?: DateTime) {
        if (luxonDateTime) {
            this.luxonDateTime = luxonDateTime
        } else {
            this.luxonDateTime = LuxonDateTime.now()
        }
    }

    public static Now() {
        return new RIODateTime(LuxonDateTime.now())
    }

    public static From(
        year: number | undefined,
        month: number | undefined,
        day: number | undefined,
        hour: number | undefined,
        minute: number | undefined,
        second: number | undefined,
    ): RIODateTime {
        const a = LuxonDateTime.fromObject({
            year: year,
            month: month,
            day: day,
            hour: hour,
            minute: minute,
            second: second,
        })

        return new RIODateTime(a)
    }

    public static FromISO(input: string, zone: string = "utc") {
        const dateTime = LuxonDateTime.fromISO(input, {
            zone,
        })
        if (!dateTime.isValid) {
            throw new Error(`Could not parse ISODateTime:${input}`)
        }
        return new RIODateTime(dateTime)
    }

    public static ParseDate(date: string, format: string) {
        let dateTimeObject = DateTime.fromFormat(date, format, {
            setZone: true,
        })
        dateTimeObject = dateTimeObject.setZone("Europe/Berlin", {
            keepLocalTime: true,
        })
        if (dateTimeObject.isValid) {
            return new RIODateTime(dateTimeObject)
        }

        throw new Error(`Could not parse date:${date} with format:${format}`)
    }

    public toISO(): string {
        return this.luxonDateTime.toISO() as string
    }

    public toISOString(timeOptions?: ToISOTimeOptions & { truncateMilliseconds?: boolean }) {
        if (timeOptions?.truncateMilliseconds) {
            return this.luxonDateTime
                .set({millisecond: 0})
                .toUTC()
                .toISO({
                    ...timeOptions,
                    suppressMilliseconds: true,
                })
        }
        return this.luxonDateTime.toUTC().toISO(timeOptions)
    }

    public toString() {
        return this.toISO()
    }

    public plus(duration: DurationLike) {
        return new RIODateTime(this.luxonDateTime.plus(duration))
    }

    public minus(duration: DurationLike) {
        return new RIODateTime(this.luxonDateTime.minus(duration))
    }

    public startOfDay() {
        return new RIODateTime(this.luxonDateTime.startOf('day'))
    }

    public endOfDay() {
        return new RIODateTime(this.luxonDateTime.endOf('day'))
    }

    public toUTC() {
        return new RIODateTime(this.luxonDateTime.toUTC())
    }

    public isSameDay(dateTime: RIODateTime) {
        const d1 = dateTime.toPredefinedFormat(RIODateTime.PREDEFINED_FORMATS.DAY_ISO)
        const d2 = this.toPredefinedFormat(RIODateTime.PREDEFINED_FORMATS.DAY_ISO)

        return d1 == d2
    }

    public epochSeconds() {
        return Math.round(this.luxonDateTime.toJSDate().getTime() / 1000)
    }

    public static isParseable(value: string) {
        const dateTime = DateTime.fromISO(value)
        return dateTime.isValid
    }

    public toPredefinedFormat(predefinedFormat: PredefinedFormats) {
        switch (predefinedFormat) {
            case PredefinedFormats.ISO_FILE:
                return this.luxonDateTime.toFormat(RIODateTime.PREDEFINED_FORMATS.ISO_FILE)

            case PredefinedFormats.DAY_ISO:
                return this.luxonDateTime.toFormat(RIODateTime.PREDEFINED_FORMATS.DAY_ISO)

            case PredefinedFormats.GERMAN_DATE:
                return this.luxonDateTime.toFormat(RIODateTime.PREDEFINED_FORMATS.GERMAN_DATE)

            case PredefinedFormats.GERMAN_DATE_TIME:
                return this.luxonDateTime.toFormat(RIODateTime.PREDEFINED_FORMATS.GERMAN_DATE_TIME)

            default:
                throw new Error(`Format:${predefinedFormat} is unknown.`)
        }
    }

    public toJSDate() {
        return this.luxonDateTime.toJSDate()
    }

    public toJSON() {
        return this.toISO()
    }

    public static ToHumanReadable(isoDate: string){
        return RIODateTime.FromISO(isoDate).toPredefinedFormat(RIODateTime.PREDEFINED_FORMATS.GERMAN_DATE_TIME)
    }

    public diff(dateTime2?: RIODateTime, unit?: DurationUnits) {
        if (!this || !dateTime2) {
            throw new Error("one or both dates are invalid.")
        }
        const date1 = this.luxonDateTime
        const date2 = dateTime2.luxonDateTime
        const diff = date1.diff(date2, unit)
        return diff.toObject()
    }
}
